import { AlertTriangle, Copy, Loader2, Send } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ShowSigningSecretDialogProps {
  secret: string;
  title: string;
  description: string;
  onClose: () => void;
  /** Present when the viewer may send a test event to this endpoint. */
  onSendTestEvent?: () => void;
  isSendingTestEvent?: boolean;
}

/**
 * Shows a new or rotated signing secret exactly once. The plaintext lives only
 * in the parent's state while this dialog is open; it is never cached, logged
 * or stored. Done stays disabled until the viewer confirms they copied it.
 */
export function ShowSigningSecretDialog({
  secret,
  title,
  description,
  onClose,
  onSendTestEvent,
  isSendingTestEvent = false,
}: ShowSigningSecretDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(secret).then(
      () => toast.success('Signing secret copied to clipboard'),
      () => toast.error('Could not copy. Select the secret and copy it manually.'),
    );
  };

  return (
    <Dialog
      open
      onOpenChange={next => {
        // Closing by Escape / overlay is allowed only once the secret is saved.
        if (!next && acknowledged) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-attention-muted text-attention-foreground border border-attention-muted/50 px-4 py-3 rounded-md text-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Shown once</p>
                <p className="mt-1">
                  Use it to verify the <span className="font-mono">webhook-signature</span> header
                  on every request. If you lose it, rotate the secret to issue a new one.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Signing secret</Label>
            <div
              className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 cursor-pointer hover:bg-muted/80"
              role="button"
              tabIndex={0}
              aria-label="Copy signing secret"
              onClick={handleCopy}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleCopy();
              }}
            >
              <code className="flex-1 text-sm font-mono break-all">{secret}</code>
              <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none pt-2">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={value => setAcknowledged(value === true)}
              className="mt-0.5"
              data-analytics-id="webhooks:secret:acknowledge"
            />
            <span className="text-sm text-muted-foreground">
              I&apos;ve copied the signing secret and stored it securely. I understand it will not
              be shown again.
            </span>
          </label>

          {onSendTestEvent && (
            <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
              <p className="font-medium">Next: send a test event</p>
              <p className="mt-1 text-muted-foreground">
                A <span className="font-mono">webhook.test</span> event checks that your receiver is
                reachable and verifies the signature.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={onSendTestEvent}
                disabled={isSendingTestEvent}
                data-analytics-id="webhooks:endpoint:send-test-from-secret"
              >
                {isSendingTestEvent ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send test event
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} disabled={!acknowledged}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
